import React, { useEffect, useRef, useState } from "react";
import { api } from "@/lib/api";
import { formatMoney } from "@/lib/format";
import { toast } from "sonner";
import { Clock, Wallet } from "lucide-react";

const STAGES = ["received", "preparing", "ready", "fulfilled"];
const STAGE_COLOR = { received: "#D85A30", preparing: "#EF9F27", ready: "#639922", fulfilled: "#378ADD" };
const NEXT = { received: "preparing", preparing: "ready", ready: "fulfilled" };
const NEXT_LABEL = { received: "Start", preparing: "Ready", ready: "Serve" };

const PAY_TILE = {
  none: "bg-white border-gray-200",
  partial: "bg-amber-50 border-amber-200",
  paid: "bg-green-50 border-green-200",
  free: "bg-gray-50 border-dashed border-gray-300",
};

function fmtDur(ms) {
  if (!isFinite(ms) || ms < 0) ms = 0;
  const s = Math.floor(ms / 1000), h = Math.floor(s / 3600), m = Math.floor((s % 3600) / 60), sec = s % 60;
  const pad = (n) => String(n).padStart(2, "0");
  return h > 0 ? `${h}:${pad(m)}:${pad(sec)}` : `${m}:${pad(sec)}`;
}

// One row per order; dots = the stages that order has reached (last dot = current stage).
function DotRows({ orders }) {
  return (
    <div className="flex flex-col gap-1">
      {orders.map((o) => {
        const reached = STAGES.slice(0, STAGES.indexOf(o.status) + 1);
        return (
          <div key={o.id} className="flex gap-1.5 items-center">
            {reached.map((s) => <span key={s} className="w-2.5 h-2.5 rounded-full" style={{ background: STAGE_COLOR[s] }} />)}
            {o.payment_status === "paid" && <span className="text-[9px] text-green-700 ml-1">paid</span>}
          </div>
        );
      })}
    </div>
  );
}

export default function AdminFloor() {
  const [floor, setFloor] = useState([]);
  const [selId, setSelId] = useState(null);
  const [now, setNow] = useState(Date.now());
  const busy = useRef(false);

  const load = () => api.get("/admin/floor").then((r) => setFloor(r.data)).catch(() => {});
  useEffect(() => { load(); const iv = setInterval(load, 5000); return () => clearInterval(iv); }, []);
  useEffect(() => { const t = setInterval(() => setNow(Date.now()), 1000); return () => clearInterval(t); }, []);

  const selected = floor.find((t) => t.id === selId) || null;

  const act = async (fn, okMsg) => {
    if (busy.current) return;
    busy.current = true;
    try { await fn(); if (okMsg) toast.success(okMsg); await load(); }
    catch (e) { toast.error(e.response?.data?.detail || "Something went wrong"); }
    finally { busy.current = false; }
  };
  const move = (o, status) => act(() => api.patch(`/admin/orders/${o.id}/status?status=${status}`));
  const pay = (o) => act(() => api.patch(`/admin/orders/${o.id}/pay`), "Marked paid");
  const settle = (t) => act(() => api.post(`/admin/tables/${t.id}/settle`), "Table settled");

  return (
    <div>
      <header className="mb-6">
        <div className="text-xs uppercase tracking-widest text-gray-400">Live</div>
        <h1 className="font-display text-2xl font-bold tracking-tight mt-1">Floor</h1>
      </header>

      <div className="grid grid-cols-1 lg:grid-cols-[1.4fr_1fr] gap-5 items-start">
        <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
          {floor.map((t) => {
            const free = t.active_count === 0;
            return (
              <button key={t.id} data-testid={`floor-tile-${t.id}`} onClick={() => setSelId(t.id)}
                className={`text-left rounded-xl border p-3 transition-colors ${PAY_TILE[t.payment_state] || PAY_TILE.none} ${selId === t.id ? "ring-2 ring-[var(--qd-primary)]" : ""} ${free ? "opacity-70" : ""}`}>
                <div className="flex items-center justify-between mb-2">
                  <span className="font-semibold">{t.label}</span>
                  {!free && <span className="text-[10px] text-gray-500 inline-flex items-center gap-1"><Clock className="w-3 h-3" aria-hidden />{fmtDur(now - Date.parse(t.oldest_created_at))}</span>}
                </div>
                {free ? (
                  <div className="text-xs text-gray-400 py-1.5">Free</div>
                ) : (
                  <>
                    <DotRows orders={t.orders} />
                    <div className="text-[11px] text-gray-500 mt-2">{t.active_count} order{t.active_count > 1 ? "s" : ""} · {formatMoney(t.total_amount)}{t.due_amount > 0 ? ` · ${formatMoney(t.due_amount)} due` : " · paid"}</div>
                  </>
                )}
              </button>
            );
          })}
          {floor.length === 0 && <div className="col-span-full text-sm text-gray-400 text-center py-10">No tables yet — add them in Settings.</div>}
        </div>

        <div className="rounded-xl border border-gray-200 bg-white p-4 sticky top-6">
          {!selected || selected.active_count === 0 ? (
            <div className="text-sm text-gray-400 text-center py-12">{selected ? `${selected.label} is free` : "Select a table to manage its orders"}</div>
          ) : (
            <>
              <div className="flex items-center justify-between mb-1">
                <span className="font-display font-bold text-lg">{selected.label}</span>
                <span className={`text-xs px-2 py-0.5 rounded ${selected.payment_state === "paid" ? "bg-green-100 text-green-700" : selected.payment_state === "partial" ? "bg-amber-100 text-amber-700" : "bg-gray-100 text-gray-600"}`}>
                  {selected.due_amount > 0 ? `${formatMoney(selected.due_amount)} due` : "All paid"}
                </span>
              </div>
              <div className="text-xs text-gray-400 mb-3">{selected.active_count} active · {formatMoney(selected.total_amount)} total</div>

              <div className="space-y-2">
                {selected.orders.map((o) => (
                  <div key={o.id} data-testid={`floor-order-${o.id}`} className="border border-gray-200 rounded-lg p-2.5">
                    <div className="flex items-center justify-between mb-1">
                      <span className="font-mono-alt text-[11px] text-gray-400">#{o.id.slice(0, 6).toUpperCase()}</span>
                      <span className="inline-flex items-center gap-1.5">
                        <span className="text-[10px] px-1.5 py-0.5 rounded text-white capitalize" style={{ background: STAGE_COLOR[o.status] }}>{o.status}</span>
                        {o.payment_status === "paid"
                          ? <span className="text-[10px] px-1.5 py-0.5 rounded bg-green-100 text-green-700">paid</span>
                          : <span className="text-[10px] px-1.5 py-0.5 rounded bg-gray-100 text-gray-500">unpaid</span>}
                      </span>
                    </div>
                    <div className="text-xs text-gray-700">
                      {o.items.map((it, i) => (
                        <div key={i}>{it.quantity}× {it.name}
                          {it.modifiers && it.modifiers.length > 0 && <span className="text-gray-400"> · {it.modifiers.map((m) => m.option_name).join(", ")}</span>}
                        </div>
                      ))}
                    </div>
                    <div className="flex items-center justify-between mt-2">
                      <span className="font-semibold text-sm">{formatMoney(o.total)}</span>
                      <span className="flex gap-1.5">
                        {NEXT[o.status] && <button data-testid={`floor-advance-${o.id}`} onClick={() => move(o, NEXT[o.status])} className="text-[11px] font-semibold px-2.5 py-1 rounded-md bg-[var(--qd-charcoal)] text-white hover:bg-black">{NEXT_LABEL[o.status]} →</button>}
                        {o.payment_status !== "paid" && <button data-testid={`floor-pay-${o.id}`} onClick={() => pay(o)} className="text-[11px] font-semibold px-2.5 py-1 rounded-md border border-green-300 text-green-700 hover:bg-green-50">Mark paid</button>}
                      </span>
                    </div>
                  </div>
                ))}
              </div>

              {selected.due_amount > 0 && (
                <button data-testid="floor-settle" onClick={() => settle(selected)} className="w-full mt-3 h-10 rounded-lg bg-[var(--qd-primary)] hover:bg-[var(--qd-primary-dark)] text-white font-semibold text-sm inline-flex items-center justify-center gap-2">
                  <Wallet className="w-4 h-4" aria-hidden /> Settle table · {formatMoney(selected.due_amount)}
                </button>
              )}
            </>
          )}
        </div>
      </div>

      <div className="mt-5 pt-3 border-t border-gray-100 flex flex-wrap gap-4 text-[11px] text-gray-400 items-center">
        <span>Tile colour = payment:</span>
        <span className="inline-flex items-center gap-1.5"><span className="w-3 h-3 rounded bg-white border border-gray-300"></span> No payment</span>
        <span className="inline-flex items-center gap-1.5"><span className="w-3 h-3 rounded bg-amber-100 border border-amber-300"></span> Partial</span>
        <span className="inline-flex items-center gap-1.5"><span className="w-3 h-3 rounded bg-green-100 border border-green-300"></span> Paid</span>
        <span className="ml-2">Dots = stages:</span>
        {STAGES.map((s) => <span key={s} className="inline-flex items-center gap-1 capitalize"><span className="w-2.5 h-2.5 rounded-full" style={{ background: STAGE_COLOR[s] }}></span>{s}</span>)}
      </div>
    </div>
  );
}
